import React from 'react';
import {
  Checkbox,
  Alert,
  Form,
  Button,
  Input,
  Collapse,
  Table,
  Tooltip,
  message,
  Modal,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import * as Yup from 'yup';
import { get as _get } from 'lodash';
import PageFormik from '../../global/page/PageFormik';
import InputWithPreview from '../../global/InputWithPreview';
import SelectWithPreview from '../../global/SelectWithPreview';
import DatePickerWithPreview from '../../global/DatePickerWithPreview';
import {
  MappedItemCustomFields,
  CustomFieldType,
  CustomFieldOption,
  ItemCustomFieldGroup,
  CustomFieldGroup,
  ManualOverwritesMap,
} from '../../../../types/custom_fields';
import Page from '../../global/page/Page';
import { localDateTimeTimeZoneString, validUrlRegExp } from '../../../utils/Utils';
import externalLink from '../../../../images/icons/external-link.svg';
import JoditHtmlEditor from '../../../containers/settings/JoditHtmlEditor';
import CustomFieldTable from './CustomFieldTable';
import ProductRelationField from './ProductRelationField';

type CustomFieldsItemTableProps = {
  fetchingData: boolean;
  customFields: MappedItemCustomFields[];
  brandId: number;
  groups: ItemCustomFieldGroup[];
  manualOverwrites: ManualOverwritesMap;
  itemId: number;
  updateCustomFields: (values: any, initialValues: any) => Promise<any>;
  runScript: (scriptId: number) => Promise<any>;
  addManualOverwrite: (itemId: number, customFieldId: number) => Promise<any>;
  removeManualOverwrite: (itemId: number, customFieldId: number) => Promise<any>;
};

const CustomFieldsItemTable: React.FC<CustomFieldsItemTableProps> = ({
  fetchingData,
  customFields,
  brandId,
  groups,
  manualOverwrites,
  itemId,
  updateCustomFields,
  runScript,
  addManualOverwrite,
  removeManualOverwrite,
}) => {
  const { t } = useTranslation();

  const [runningScriptIds, setRunningScriptIds] = React.useState<number[]>([]);
  const [keywords, setKeywords] = React.useState('');
  const [checkboxLoadingStates, setCheckboxLoadingStates] = React.useState<{
    [key: string]: boolean;
  }>({});

  const addRunningId = (id: number) => setRunningScriptIds([...runningScriptIds, id]);
  const removeRunningId = (id: number) =>
    setRunningScriptIds(runningScriptIds => runningScriptIds.filter(rId => rId !== id));

  const isManuallyOverridden = (customFieldId: number) => {
    const itemIds = manualOverwrites[customFieldId] || [];
    return itemIds.includes(itemId);
  };

  const handleOverrideToggle = async (checked: boolean, customFieldId: number) => {
    const key = `${customFieldId}_${itemId}`;
    setCheckboxLoadingStates(prev => ({ ...prev, [key]: true }));

    try {
      if (checked) {
        await addManualOverwrite(itemId, customFieldId);
      } else {
        await removeManualOverwrite(itemId, customFieldId);
      }
    } catch (error) {
      message.error(t('settings:customFields.manualOverrideError'));
    } finally {
      setCheckboxLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleKeywordSearch = (filterKeywords: string) => {
    setKeywords(filterKeywords);
  };

  const getValidation = () => {
    return Yup.object().shape({
      customFields: Yup.array().of(
        Yup.object().shape({
          custom_field_type_id: Yup.number(),
          value: Yup.string().when('custom_field_type_id', {
            is: (value: number) => value === CustomFieldType.Link,
            then: schema => schema.matches(validUrlRegExp, t('validation:enterValidUrl')),
          }),
        })
      ),
    });
  };

  return customFields.length === 0 ? (
    <Page showExpandButton>
      <Alert
        message={t('segments:customFields.infoTitle')}
        description={
          <div>
            <Trans i18nKey="segments:customFields.goToSettings">
              Go to{' '}
              <Link
                to={{
                  pathname: brandId ? '/brand/settings/custom-fields' : '/settings/custom-fields',
                  search: brandId ? `brandId=${brandId}` : '',
                }}
              >
                Settings
              </Link>
              for adding and configuring your custom fields.
            </Trans>
          </div>
        }
        type="info"
      />
    </Page>
  ) : (
    <PageFormik
      initialValues={{ groups }}
      fetchingData={fetchingData}
      onSubmit={(values, { setSubmitPending, setSubmitSuccess, setSubmitError }) => {
        setSubmitPending();
        updateCustomFields(values, { groups })
          .then(() => setSubmitSuccess())
          .catch(error => {
            // Handle 422 validation errors
            if (error.response?.status === 422 && error.response?.data?.validation_errors) {
              const validationErrors = error.response.data.validation_errors;

              // Build a mapping of custom_field_id to field name
              const fieldNameMap: { [key: number]: string } = {};
              values.groups.forEach((group: ItemCustomFieldGroup) => {
                group.fields.forEach((field: MappedItemCustomFields) => {
                  fieldNameMap[Number(field.id)] = field.name;
                });
              });

              // Build error content
              if (validationErrors.length === 1) {
                const validationError = validationErrors[0];
                const fieldName =
                  fieldNameMap[validationError.custom_field_id] ||
                  `ID: ${validationError.custom_field_id}`;
                const errorMessages = validationError.errors.join('; ');
                message.error(
                  `${t('segments:customFields.validationFailedField')}: ${fieldName}\n${t(
                    'segments:customFields.validationFailedValue'
                  )}: "${validationError.value}"\n${errorMessages}`,
                  8
                );
              } else {
                // Multiple errors - show in modal
                const errorContent = (
                  <div>
                    {validationErrors.map((error: any, index: number) => {
                      const fieldName =
                        fieldNameMap[error.custom_field_id] || `ID: ${error.custom_field_id}`;
                      return (
                        <div key={index} className="cfs__validation-error">
                          <div>
                            <strong>{t('segments:customFields.validationFailedField')}:</strong>{' '}
                            {fieldName}
                          </div>
                          <div>
                            <strong>{t('segments:customFields.validationFailedValue')}:</strong>{' '}
                            &quot;
                            {error.value}&quot;
                          </div>
                          <div>
                            <strong>{t('segments:customFields.validationErrors')}:</strong>
                          </div>
                          <ul className="cfs__validation-error-list">
                            {error.errors.map((err: string, errIdx: number) => (
                              <li key={errIdx}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                );

                Modal.error({
                  title: t('segments:customFields.validationFailedTitle'),
                  content: errorContent,
                  width: 600,
                });
              }
            } else {
              // Generic error message for non-422 errors
              message.error(t('common:error'));
            }
            setSubmitError();
          });
      }}
      contentNoSpacing
      showAnalysis
      showExpandButton
      validationSchema={getValidation()}
      enableReinitialize
    >
      {({ setFieldValue, setFieldTouched, validateForm, values, dirty, errors, touched }) => {
        const filteredGroups = keywords
          ? values.groups.map((g: CustomFieldGroup) => {
              const filteredFields = g.fields.filter(({ name }) =>
                name.toLowerCase().includes(keywords.toLowerCase())
              );
              return { ...g, fields: filteredFields };
            })
          : values.groups;

        const nameCellRenderer = (value: any, record: MappedItemCustomFields) => {
          const isOverridden = isManuallyOverridden(Number(record.id));
          const checkboxKey = `${record.id}_${itemId}`;
          const isLoading = checkboxLoadingStates[checkboxKey] || false;

          const getTooltipContent = () => {
            if (!record.ai_configuration && !record.dynamic_configuration) {
              return null;
            }

            let baseMessage;
            if (isOverridden) {
              baseMessage = t('settings:customFields.manualOverrideEnabled');
            } else if (record.ai_configuration) {
              baseMessage = t('settings:customFields.aiFieldInfo');
            } else {
              baseMessage = t('settings:customFields.dynamicFieldInfo');
            }

            const lastUpdated = `${t('common:lastUpdatedAt')}:\n${localDateTimeTimeZoneString(
              record.updated_at
            )}`;

            return (
              <div onClick={e => e.stopPropagation()}>
                <div style={{ whiteSpace: 'pre-line', marginBottom: 8 }}>
                  {baseMessage}
                  {'\n\n'}
                  {lastUpdated}
                </div>
                <Checkbox
                  checked={isOverridden}
                  disabled={isLoading}
                  onChange={e => handleOverrideToggle(e.target.checked, Number(record.id))}
                  style={{ color: 'white' }}
                >
                  <span style={{ color: 'white' }}>
                    {t('settings:customFields.manualOverrideCheckboxLabel')}
                  </span>
                </Checkbox>
              </div>
            );
          };

          const tooltipContent = getTooltipContent();

          return (
            <>
              {value}
              {(!!record.dynamic_configuration || !!record.ai_configuration) && (
                <Tooltip title={tooltipContent} className="align-top" trigger={['hover', 'click']}>
                  <InfoCircleOutlined className="ant-info-tooltip pl-2" />
                </Tooltip>
              )}
            </>
          );
        };

        const columnCellRenderer = (
          value: any,
          record: MappedItemCustomFields,
          index: number,
          groupIndex: number
        ) => {
          const fullDataRowIndex = values.groups[groupIndex].fields.findIndex(
            (f: MappedItemCustomFields) => f.id === record.id
          );
          const cutomFieldTypeId = record.custom_field_type_id;
          const formikIndex = `groups[${groupIndex}].fields[${fullDataRowIndex}].value`;

          const isOverridden = isManuallyOverridden(Number(record.id));
          const shouldDisable =
            !isOverridden &&
            (record.read_only_custom_field === 1 ||
              !!record.dynamic_configuration ||
              !!record.ai_configuration);

          return (
            <div>
              {cutomFieldTypeId === CustomFieldType.Input && (
                <InputWithPreview
                  value={value}
                  onChange={e => setFieldValue(formikIndex, e.target.value)}
                  showCount
                  disabled={shouldDisable}
                  tabIndex={0}
                />
              )}
              {cutomFieldTypeId === CustomFieldType.Number && (
                <InputWithPreview
                  type="number"
                  value={value}
                  onChange={e => setFieldValue(formikIndex, e.target.value)}
                  showCount
                  disabled={shouldDisable}
                  tabIndex={0}
                />
              )}
              {cutomFieldTypeId === CustomFieldType.Dropdown && (
                <SelectWithPreview
                  value={value}
                  onChange={(val: string | undefined) => setFieldValue(formikIndex, val || '')}
                  allowClear={record.read_only_custom_field === 1}
                  disabled={shouldDisable}
                  showSearch
                  options={record.options.map((option: CustomFieldOption) => ({
                    key: option.id,
                    value: option.name,
                    label: option.name,
                  }))}
                  tabIndex={0}
                />
              )}
              {cutomFieldTypeId === CustomFieldType.Checkbox && (
                <Checkbox
                  style={{ marginLeft: 12 }}
                  checked={value === '1'}
                  onChange={e => setFieldValue(formikIndex, e.target.checked ? '1' : '')}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setFieldValue(formikIndex, value === '1' ? '' : '1');
                    }
                  }}
                  disabled={shouldDisable}
                  tabIndex={0}
                />
              )}
              {cutomFieldTypeId === CustomFieldType.Text_Editor && (
                <JoditHtmlEditor
                  htmlText={value}
                  dirty={dirty}
                  handleOnChange={html => {
                    setFieldValue(formikIndex, html);
                  }}
                  readonly={shouldDisable}
                  tabIndex={0}
                />
              )}

              {cutomFieldTypeId === CustomFieldType.Link && (
                //  use lodash because name can include dots eg. foo.bar
                <div className="flex flex-row mt-px cf__https">
                  <Form.Item
                    hasFeedback
                    validateStatus={
                      _get(errors, formikIndex) && _get(touched, formikIndex) ? 'error' : ''
                    }
                    help={
                      (_get(touched, formikIndex) && _get(errors, formikIndex)?.toString()) ||
                      undefined
                    }
                    className="flex-1"
                  >
                    <InputWithPreview
                      placeholder="https://"
                      value={value}
                      onChange={e => {
                        setFieldValue(formikIndex, e.target.value);
                      }}
                      displayError={!!(_get(errors, formikIndex) && _get(touched, formikIndex))}
                      onBlur={() => {
                        setFieldTouched(formikIndex);
                        validateForm();
                      }}
                      showCount
                      disabled={shouldDisable}
                      tabIndex={0}
                    />
                  </Form.Item>
                  {validUrlRegExp.test(value) && (
                    <Button
                      className="cfs__external-link ml-1"
                      type="link"
                      href={value.startsWith('http') ? value : `//${value}`}
                      rel="noopener noreferrer"
                      target="_blank"
                      icon={<img src={externalLink} alt="" />}
                      disabled={shouldDisable}
                    />
                  )}
                </div>
              )}
              {cutomFieldTypeId === CustomFieldType.Button && (
                <Button
                  onClick={async () => {
                    addRunningId(Number(record.id));
                    await runScript(record.script_id!);
                    removeRunningId(Number(record.id));
                  }}
                  loading={runningScriptIds.includes(Number(record.id))}
                  disabled={shouldDisable}
                  tabIndex={0}
                >
                  {t('api:runScript')}
                </Button>
              )}
              {cutomFieldTypeId === CustomFieldType.Table && (
                <CustomFieldTable
                  value={value}
                  headers={record.headers}
                  tableDelimiter={record.table_delimiter}
                  tableRowDelimiter={record.table_row_delimiter}
                  onChange={(newValue: string) => setFieldValue(formikIndex, newValue)}
                  disabled={
                    record.read_only_custom_field === 1 ||
                    !!record.dynamic_configuration ||
                    !!record.ai_configuration
                  }
                />
              )}
              {cutomFieldTypeId === CustomFieldType.Product_Relation && (
                <ProductRelationField
                  value={value}
                  productRelationDelimiter={record.product_relation_delimiter || ','}
                  productRelationBrandCodeDelimiter={record.product_relation_brand_code_delimiter}
                  onChange={(newValue: string) => setFieldValue(formikIndex, newValue)}
                  disabled={shouldDisable}
                />
              )}
              {cutomFieldTypeId === CustomFieldType.Date && (
                <DatePickerWithPreview
                  value={value || null}
                  onChange={date => {
                    const formattedDate = date ? date.format('YYYY-MM-DD') : '';
                    setFieldValue(formikIndex, formattedDate);
                  }}
                  disabled={shouldDisable}
                  allowClear
                  tabIndex={0}
                />
              )}
            </div>
          );
        };

        return (
          <div className="overflow-hidden h-full flex flex-col">
            <div className="flex ml-3 mt-3 mb-2">
              <Input.Search
                className="cfs__item-top-bar-search"
                value={keywords}
                onChange={e => handleKeywordSearch(e.target.value)}
                placeholder={t('settings:customFields.searchCustomFields')}
                size="small"
                allowClear
              />
            </div>
            <div className="cf__wrapper flex-1 overflow-auto">
              <Collapse
                className="cf__collapse"
                defaultActiveKey={groups.map(g => g.id)}
                onChange={() => {}}
              >
                {filteredGroups.map((group: ItemCustomFieldGroup, groupIndex: number) => (
                  <Collapse.Panel header={group.name} key={group.id}>
                    <Table
                      dataSource={group.fields}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      locale={{ emptyText: t('common:noData') }}
                      className={group.id === 'sharedbyBrand' ? 'cf__sharedbyBrand' : undefined}
                    >
                      <Table.Column
                        title={t('common:name')}
                        dataIndex="name"
                        render={(value: any, record: MappedItemCustomFields) =>
                          nameCellRenderer(value, record)
                        }
                        width={300}
                      />
                      <Table.Column
                        title={t('common:value')}
                        dataIndex="value"
                        render={(value: any, record: MappedItemCustomFields, index: number) =>
                          columnCellRenderer(value, record, index, groupIndex)
                        }
                      />
                    </Table>
                  </Collapse.Panel>
                ))}
              </Collapse>
            </div>
          </div>
        );
      }}
    </PageFormik>
  );
};

export default CustomFieldsItemTable;
